import React, { useState, useEffect } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Checkbox } from '../ui/Checkbox';
import { useAuth } from '../../lib/AuthContext';

interface EmailSettings {
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  smtpUser: string;
  smtpPassword: string;
  fromEmail: string;
  fromName: string;
  notificationEmails: string[];
  enabled: boolean;
}

const EmailNotificationSettings: React.FC = () => {
  const { currentUser } = useAuth();
  const [settings, setSettings] = useState<EmailSettings>({
    smtpHost: '',
    smtpPort: 587,
    smtpSecure: false,
    smtpUser: '',
    smtpPassword: '',
    fromEmail: '',
    fromName: 'Questionnaire System',
    notificationEmails: [],
    enabled: false
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [testResult, setTestResult] = useState<{type: 'success' | 'error', message: string} | null>(null);

  useEffect(() => {
    loadSettings();
  }, [currentUser]); // Add currentUser as dependency since loadSettings depends on it

  const loadSettings = async () => {
    try {
      if (!currentUser) return;
      
      const token = await currentUser.getIdToken();
      
      const response = await fetch('https://api-reuvze3ckq-uc.a.run.app/api/settings/email-notifications', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      } else {
        const errorText = await response.text();
        console.error('Failed to load email settings:', errorText);
      }
    } catch (error) {
      console.error('Error loading email settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      if (!currentUser) return;
      
      setSaving(true);
      const token = await currentUser.getIdToken();
      
      
      const response = await fetch('https://api-reuvze3ckq-uc.a.run.app/api/settings/email-notifications', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        setTestResult({ type: 'success', message: 'Email settings saved successfully!' });
        setTimeout(() => setTestResult(null), 3000);
      } else {
        const errorText = await response.text();
        try {
          const error = JSON.parse(errorText);
          setTestResult({ type: 'error', message: error.error || 'Failed to save settings' });
        } catch {
          setTestResult({ type: 'error', message: 'Failed to save settings: ' + errorText.substring(0, 100) });
        }
      }
    } catch (error) {
      console.error('Error saving email settings:', error);
      setTestResult({ type: 'error', message: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const testConfiguration = async () => {
    console.log('Test Configuration button clicked');
    try {
      if (!currentUser) {
        console.log('No current user for test configuration');
        return;
      }
      
      setTesting(true);
      console.log('Getting auth token for test...');
      const token = await currentUser.getIdToken();
      
      const response = await fetch('https://api-reuvze3ckq-uc.a.run.app/api/settings/email-notifications/test', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setTestResult({ type: 'success', message: 'Email configuration test successful!' });
        } else {
          setTestResult({ type: 'error', message: result.message || 'Email test failed' });
        }
      } else {
        const errorText = await response.text();
        try {
          const error = JSON.parse(errorText);
          setTestResult({ type: 'error', message: error.error || 'Email test failed' });
        } catch {
          setTestResult({ type: 'error', message: 'Email test failed: ' + errorText.substring(0, 100) });
        }
      }
    } catch (error) {
      console.error('Error testing email configuration:', error);
      setTestResult({ type: 'error', message: 'Failed to test email configuration' });
    } finally {
      setTesting(false);
    }
  };

  const sendTestEmail = async () => {
    console.log('Send Test Email button clicked');
    try {
      if (!currentUser) {
        console.log('No current user for send test email');
        return;
      }
      
      setTesting(true);
      console.log('Getting auth token for test email...');
      const token = await currentUser.getIdToken();
      
      const response = await fetch('https://api-reuvze3ckq-uc.a.run.app/api/settings/email-notifications/send-test', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setTestResult({ type: 'success', message: 'Test email sent successfully! Check your inbox.' });
        } else {
          setTestResult({ type: 'error', message: result.message || 'Failed to send test email' });
        }
      } else {
        const errorText = await response.text();
        try {
          const error = JSON.parse(errorText);
          setTestResult({ type: 'error', message: error.error || 'Failed to send test email' });
        } catch {
          setTestResult({ type: 'error', message: 'Failed to send test email: ' + errorText.substring(0, 100) });
        }
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      setTestResult({ type: 'error', message: 'Failed to send test email' });
    } finally {
      setTesting(false);
    }
  };

  const addNotificationEmail = () => {
    if (newEmail && newEmail.includes('@') && !settings.notificationEmails.includes(newEmail)) {
      setSettings(prev => ({
        ...prev,
        notificationEmails: [...prev.notificationEmails, newEmail]
      }));
      setNewEmail('');
    }
  };

  const removeNotificationEmail = (email: string) => {
    setSettings(prev => ({
      ...prev,
      notificationEmails: prev.notificationEmails.filter(e => e !== email)
    }));
  };

  const handleInputChange = (field: keyof EmailSettings, value: any) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-600">Loading email settings...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Email Notification Settings</h2>
        <p className="text-gray-600 mt-2">
          Configure email notifications for questionnaire submissions. Notifications will be sent to all specified email addresses when users complete questionnaires.
        </p>
      </div>

      {testResult && (
        <div className={`p-4 rounded-md ${testResult.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
          {testResult.message}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Enable Notifications</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-2">
            <Checkbox
              checked={settings.enabled}
              onCheckedChange={(checked) => handleInputChange('enabled', checked)}
            />
            <label className="text-sm font-medium">
              Enable email notifications for questionnaire submissions
            </label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>SMTP Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SMTP Host
              </label>
              <Input
                type="text"
                value={settings.smtpHost}
                onChange={(e) => handleInputChange('smtpHost', e.target.value)}
                placeholder="smtp.gmail.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SMTP Port
              </label>
              <Input
                type="number"
                value={settings.smtpPort}
                onChange={(e) => handleInputChange('smtpPort', parseInt(e.target.value))}
                placeholder="587"
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              checked={settings.smtpSecure}
              onCheckedChange={(checked) => handleInputChange('smtpSecure', checked)}
            />
            <label className="text-sm font-medium">
              Use SSL/TLS (typically false for port 587, true for port 465)
            </label>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SMTP Username
              </label>
              <Input
                type="text"
                value={settings.smtpUser}
                onChange={(e) => handleInputChange('smtpUser', e.target.value)}
                placeholder="your-email@gmail.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                SMTP Password
              </label>
              <Input
                type="password"
                value={settings.smtpPassword}
                onChange={(e) => handleInputChange('smtpPassword', e.target.value)}
                placeholder="your-app-password"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Email Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                From Email Address
              </label>
              <Input
                type="email"
                value={settings.fromEmail}
                onChange={(e) => handleInputChange('fromEmail', e.target.value)}
                placeholder="notifications@yourcompany.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                From Name
              </label>
              <Input
                type="text"
                value={settings.fromName}
                onChange={(e) => handleInputChange('fromName', e.target.value)}
                placeholder="Questionnaire System"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Notification Recipients</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">
            Add email addresses that should receive notifications when questionnaires are submitted.
          </p>
          
          <div className="flex space-x-2">
            <Input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="admin@yourcompany.com"
              onKeyPress={(e) => e.key === 'Enter' && addNotificationEmail()}
            />
            <Button 
              onClick={addNotificationEmail}
              disabled={!newEmail || !newEmail.includes('@')}
              className="whitespace-nowrap px-6"
            >
              Add Email
            </Button>
          </div>

          {settings.notificationEmails.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700">Current Recipients:</h4>
              <div className="space-y-2">
                {settings.notificationEmails.map((email, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                    <span className="text-sm">{email}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeNotificationEmail(email)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {settings.notificationEmails.length === 0 && (
            <div className="text-sm text-gray-500 italic">
              No notification recipients configured. Add email addresses above.
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex space-x-4">
        <Button 
          onClick={saveSettings} 
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
        
        <Button 
          onClick={testConfiguration} 
          disabled={testing || !settings.enabled}
          variant="outline"
        >
          {testing ? 'Testing...' : 'Test Configuration'}
        </Button>
        
        <Button 
          onClick={sendTestEmail} 
          disabled={testing || !settings.enabled}
          variant="outline"
          className="bg-green-50 hover:bg-green-100 text-green-700 border-green-300"
        >
          {testing ? 'Sending...' : 'Send Test Email'}
        </Button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <h4 className="text-sm font-medium text-blue-800 mb-2">Setup Tips:</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• For Gmail: Use port 587 with SSL/TLS disabled, and generate an App Password</li>
          <li>• For Outlook: Use smtp.live.com with port 587</li>
          <li>• Test your configuration before enabling notifications</li>
          <li>• Email failures won't prevent questionnaire submissions from completing</li>
        </ul>
      </div>
    </div>
  );
};

export default EmailNotificationSettings;
